import {
  ApprovalProject,
  FinancialSummaryInfo,
  ProjectsApprovalResponse,
} from './types';
import { getFY } from '../../utils/calculation';
import { InvestmentItem, TableProject } from '../../models/project';
import {
  formatToThousands,
  mapFinancialSummaryInfo,
  returnFormattedForecastsForFutureFys,
} from '../utils/utils';
import { getTertial } from '../../utils/utils';
import { returnCurrencyObject } from '../../hooks/useLocalCurrency';
import getSymbolFromCurrency from 'currency-symbol-map';
import { calculateStats } from '../projects/utils';

const formatThisYearBaseline = (infos: FinancialSummaryInfo[]) => {
  const thisFy = getFY();
  const thisBaseline = infos?.find(
    (info) =>
      info.financialCategory === 'BASELINE' && info.fiscalYear === thisFy,
  );
  if (thisBaseline) {
    const baselineObject = {
      [`baseline-${thisFy}-retailCost`]: formatToThousands(
        thisBaseline.retailCost,
      ),
      [`baseline-${thisFy}-landCost`]: formatToThousands(thisBaseline.landCost),
      [`baseline-${thisFy}-propertyInvestment`]: formatToThousands(
        thisBaseline.propertyInvestment,
      ),
      [`baseline-${thisFy}-retailInvestment`]: formatToThousands(
        thisBaseline.retailInvestment,
      ),
    };
    return baselineObject;
  }
  return {};
};

const isNotNull = (value) => value !== null;
const mergeBaselineInfos = (
  existingInfos: FinancialSummaryInfo[],
  newInfos: FinancialSummaryInfo[],
): FinancialSummaryInfo[] => {
  const merged = [...existingInfos];
  newInfos.forEach((update) => {
    const existingIndex = merged.findIndex(
      (info) =>
        info.fiscalYear === update.fiscalYear &&
        info.financialCategory === update.financialCategory,
    );
    if (existingIndex !== -1) {
      merged[existingIndex] = { ...update };
    } else {
      merged.push(update);
    }
  });
  return merged;
};

export const updateProjectApprovalFields = (
  projects: ApprovalProject[],
  approvalFields: ProjectsApprovalResponse,
) => {
  return projects.map((project) => {
    const {
      projectIds,
      ocnPriorityType,
      fiscalYearAlignedWithGEXList,
      gexComment,
      isep,
      partOfFda,
      partOfFdaDate,
      fundedBy,
      isepStatus,
      validationComment,
      subPortfolio,
      validation,
      financialSummaryInfos,
    } = approvalFields;
    if (projectIds.includes(project.id)) {
      const newBaselineInfos = (financialSummaryInfos || []).filter(
        (info) =>
          info.projectId === project.id &&
          info.financialCategory === 'BASELINE',
      );
      const updatedFinancialSummaryInfos =
        newBaselineInfos.length > 0
          ? mergeBaselineInfos(
              project.financialSummaryInfos || [],
              newBaselineInfos,
            )
          : project.financialSummaryInfos;
      return {
        ...project,
        fiscalYearAlignedWithGEXList: fiscalYearAlignedWithGEXList?.filter(
          (gexItem) => gexItem?.projectId === project?.id,
        ),
        ocnPriority: ocnPriorityType,
        gexComment: isNotNull(gexComment) ? gexComment : project?.gexComment,
        isepStatus: isNotNull(isepStatus) ? isepStatus : project?.isepStatus,
        validationComment: isNotNull(validationComment)
          ? validationComment
          : project?.validationComment,
        subPortfolio: isNotNull(subPortfolio)
          ? subPortfolio
          : project?.subPortfolio,
        validation: isNotNull(validation) ? validation : project?.validation,
        fundedBy: isNotNull(fundedBy) ? fundedBy : project?.fundedBy,
        isep: isNotNull(isep) ? isep : project?.isep,
        isepDate:
          project?.isep && isep
            ? project?.isepDate
            : isep
              ? new Date().toISOString().split('T')[0]
              : project?.isepDate,
        partOfFda: isNotNull(partOfFda) ? partOfFda : project?.partOfFda,
        partOfFdaDate: isNotNull(partOfFdaDate)
          ? partOfFdaDate
          : project?.partOfFdaDate,
        financialSummaryInfos: updatedFinancialSummaryInfos,
      };
    }
    return project;
  });
};

const returnFormattedInvestments = (
  investments: Partial<InvestmentItem>[] = [],
) => {
  const object = {};
  const years = [getFY(0), getFY(1), getFY(2), getFY(3), getFY(4), getFY(5)];
  years.forEach((year) => {
    const investment = investments?.find(
      (investment) => investment.fiscalYear === year,
    );
    if (!investment) return;
    object[`land-${year}`] = formatToThousands(investment.landCost);
    object[`property-${year}`] = formatToThousands(
      investment.propertyInvestment,
    );
    object[`retail-${year}`] = formatToThousands(investment.retailInvestment);
    object[`retailCost-${year}`] = formatToThousands(investment.retailCost);
    object[`total-${year}`] = formatToThousands(investment.totalInvestment);
  });
  return object;
};

const returnFormattedForecast = (
  forecast: FinancialSummaryInfo,
  currencyRate,
) => {
  return {
    forecastLandCost: formatToThousands(forecast.landCost * currencyRate),
    forecastPropertyInvestment: formatToThousands(
      forecast.propertyInvestment * currencyRate,
    ),
    forecastRetailInvestment: formatToThousands(
      forecast.retailInvestment * currencyRate,
    ),
    forecastRetailCost: formatToThousands(forecast.retailCost * currencyRate),
    totalForecast: formatToThousands(forecast.totalInvestment * currencyRate),
  };
};

export const enrichFinancialSummaryInfosWithEur = (
  infos: FinancialSummaryInfo[],
  currencies: object,
  countryCode: string,
): FinancialSummaryInfo[] => {
  if (!infos?.length) return infos;
  return infos.map((info) => {
    const currencyRate =
      returnCurrencyObject(currencies, countryCode, info.fiscalYear)
        ?.currencyRate || 1;
    return {
      ...info,
      landCostEur: parseFloat((info.landCost * currencyRate).toFixed(2)),
      propertyInvestmentEur: parseFloat(
        (info.propertyInvestment * currencyRate).toFixed(2),
      ),
      retailInvestmentEur: parseFloat(
        (info.retailInvestment * currencyRate).toFixed(2),
      ),
      retailCostEur: parseFloat((info.retailCost * currencyRate).toFixed(2)),
      totalInvestmentEur: parseFloat(
        (info.totalInvestment * currencyRate).toFixed(2),
      ),
    };
  });
};

export const formatTotalFinancials = (
  project: ApprovalProject | TableProject,
) => {
  const totalsObject = {};
  Object.keys(project.financialInfo || {}).forEach((key) => {
    totalsObject[key] = formatToThousands(project.financialInfo[key]);
  });
  return totalsObject;
};

export const formatApprovalProjects = (
  projects: ApprovalProject[],
  currencies: object,
) =>
  projects
    .map((project) => ({
      ...project,
      financialSummaryInfos: enrichFinancialSummaryInfosWithEur(
        project.financialSummaryInfos,
        currencies,
        project.countryCode,
      ),
      currencySymbol: getSymbolFromCurrency(
        returnCurrencyObject(
          currencies,
          project.countryCode,
          project.financialSummaryInfos?.[0]?.fiscalYear,
        )?.currencyCode || 'EUR',
      ),
      ...returnFormattedInvestments(project.investments || []),
      ...mapFinancialSummaryInfo(project.financialSummaryInfo),
      ...(project.financialSummaryInfos?.[0] &&
        returnFormattedForecast(
          project.financialSummaryInfos?.[0],
          returnCurrencyObject(
            currencies,
            project.countryCode,
            project.financialSummaryInfos?.[0].fiscalYear,
          )?.currencyRate,
        )),
      ...returnFormattedForecastsForFutureFys(project, currencies),
      ...formatTotalFinancials(project),
    }))
    .map((project) => {
      return {
        ...project,
        confidential: project.confidential || 'INTERNAL',
        solutionCategory: project?.solutions
          ?.map((solution) => solution.solutionCategory)
          .join(', '),
        solutionType: project?.solutions
          ?.map((solution) => solution.solutionType)
          .join(', '),
        solutionPurpose: project?.solutions
          ?.map((solution) => solution.solutionPurpose)
          .join(', '),
        projectManagerLeader: project?.projectResponsibleList?.find(
          (person) => person?.projectResponsibleRole === 'PROJECT_MANAGER',
        )?.projectResponsiblePerson,
        projectAdmin: project?.projectResponsibleList?.find(
          (person) => person?.projectResponsibleRole === 'PROJECT_ADMIN',
        )?.projectResponsiblePerson,
        projectOwner: project?.projectResponsibleList?.find(
          (person) => person?.projectResponsibleRole === 'PPROJECT_OWNER',
        )?.projectResponsiblePerson,
        projectOrderer: project?.projectResponsibleList?.find(
          (person) => person?.projectResponsibleRole === 'PROJECT_ORDERER',
        )?.projectResponsiblePerson,
        isLeasedFromAnotherIngkaCompany:
          project?.isLeasedFromAnotherIngkaCompany,
        openingFYTertial: `${getFY(0, new Date(project.targetProjectEndDate))}-T${getTertial(new Date(project.targetProjectStartDate))}`,
        stats: calculateStats(project),
        alignedWithGEXFY: project?.fiscalYearAlignedWithGEXList?.reduce(
          (acc, fy) => {
            if (fy.baselineStartDate && fy.fiscalYear) {
              acc[fy.fiscalYear] = `${fy.alignedWithGEXType}`;
            }
            return acc;
          },
          {} as Record<string, string>,
        ),
        alignedWithGEXFYDates: project?.fiscalYearAlignedWithGEXList?.reduce(
          (acc, fy) => {
            if (fy.baselineStartDate && fy.fiscalYear) {
              acc[fy.fiscalYear] =
                `${fy.alignedWithGEXType}: ${fy.baselineStartDate} - ${fy.baselineOpeningDate}`;
            }
            return acc;
          },
          {} as Record<string, string>,
        ),
        baselineByFY: (project?.financialSummaryInfos || []).reduce(
          (acc, info) => {
            if (
              info.financialCategory === 'BASELINE' &&
              info.fiscalYear &&
              typeof info.totalInvestment !== 'undefined'
            ) {
              acc[info.fiscalYear] = formatToThousands(info.totalInvestment);
            }
            return acc;
          },
          {} as Record<string, string>,
        ),
        ...formatThisYearBaseline(project?.financialSummaryInfos || []),
        fyBudgetFYs: Object.entries(project.forecastSummary || {})
          .filter(([, value]) => (value as number) > 0)
          .map(([fy]) => fy),
      };
    });
